import { useMemo, useState } from "react";
import SectionHeader from "@/components/feature/panel/SectionHeader";
import EmptyState from "@/components/feature/panel/EmptyState";
import { useDataQuery } from "@/hooks/useDataQuery";
import AccountRemoval, {accountAdmin} from "@/components/feature/AccountRemoval";
import UserDetailModal from "./components/UserDetailModal";
import UserEditModal from "./components/UserEditModal";
import TeacherAddModal from "./components/TeacherAddModal";
import { ROLE_META, formatDate, initialsOf, type Profile } from "./userMeta";

type RoleFilter = "tumu" | "ogrenci" | "veli" | "ogretmen";

const FILTER_TABS: { key: RoleFilter; label: string }[] = [
  { key: "tumu", label: "Tümü" },
  { key: "ogrenci", label: "Öğrenciler" },
  { key: "veli", label: "Veliler" },
  { key: "ogretmen", label: "Öğretmenler" },
];

export default function AdminKullanicilar() {
  const [showRemoved,setShowRemoved] = useState(false);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("tumu");
  const [detailProfile, setDetailProfile] = useState<Profile | null>(null);
  const [editProfile, setEditProfile] = useState<Profile | null>(null);
  const [addTeacherOpen, setAddTeacherOpen] = useState(false);

  const { data, loading, error, reload } = useDataQuery<Profile[]>(async () => {
    const result=await accountAdmin();
    return result.users as Profile[];
  }, []);

  const filtered = useMemo(() => {
    const list = data ?? [];
    return list.filter((p) => {
      if (!!p.removed_at !== showRemoved) return false;
      if (roleFilter !== "tumu" && p.role !== roleFilter) return false;
      if (!query.trim()) return true;
      const q = query.trim().toLocaleLowerCase("tr-TR");
      return (
        (p.full_name ?? "").toLocaleLowerCase("tr-TR").includes(q) ||
        (p.email ?? "").toLocaleLowerCase("tr-TR").includes(q) ||
        (p.grade ?? "").toLocaleLowerCase("tr-TR").includes(q) ||
        (p.city ?? "").toLocaleLowerCase("tr-TR").includes(q)
      );
    });
  }, [data, query, roleFilter, showRemoved]);

  const roleCount = (role: string) =>
    (data ?? []).filter((p) => !p.removed_at && p.role === role).length;

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Kullanıcılar"
        description="Platforma kayıt olan öğrenci, veli ve öğretmen hesaplarını görüntüle ve düzenle."
      />

      <button className="border rounded px-4 py-2" onClick={()=>setShowRemoved(!showRemoved)}>{showRemoved?"Aktif listeye dön":"Silinenler"}</button>
      {/* Özet kartlar */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 shrink-0">
              <i className="ri-graduation-cap-line text-lg" aria-hidden="true"></i>
            </span>
            <div>
              <p className="text-2xl font-heading font-bold text-foreground-950">
                {roleCount("ogrenci")}
              </p>
              <p className="text-sm text-foreground-500">Öğrenci</p>
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-100 text-accent-800 shrink-0">
              <i className="ri-heart-3-line text-lg" aria-hidden="true"></i>
            </span>
            <div>
              <p className="text-2xl font-heading font-bold text-foreground-950">
                {roleCount("veli")}
              </p>
              <p className="text-sm text-foreground-500">Veli</p>
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-900 shrink-0">
              <i className="ri-user-star-line text-lg" aria-hidden="true"></i>
            </span>
            <div>
              <p className="text-2xl font-heading font-bold text-foreground-950">
                {roleCount("ogretmen")}
              </p>
              <p className="text-sm text-foreground-500">Öğretmen</p>
            </div>
          </div>
        </div>
      </div>

      {/* Arama + filtre */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400">
            <i className="ri-search-line text-sm" aria-hidden="true"></i>
          </span>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="İsim, e-posta, sınıf veya il ara..."
            className="w-full h-10 pl-9 pr-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none transition-colors"
          />
        </div>

        <div className="flex p-1 rounded-full bg-background-100" role="tablist" aria-label="Rol filtresi">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={roleFilter === tab.key}
              onClick={() => setRoleFilter(tab.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                roleFilter === tab.key
                  ? "bg-background-50 text-primary-700"
                  : "text-foreground-500 hover:text-foreground-800"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => setAddTeacherOpen(true)}
          className="sm:ml-auto inline-flex items-center gap-2 h-10 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-user-add-line text-base" aria-hidden="true"></i>
          Öğretmen Ekle
        </button>
      </div>

      {/* İçerik */}
      {loading ? (
        <div className="rounded-lg border border-background-200 bg-background-50 p-10 flex items-center justify-center text-foreground-500">
          <i className="ri-loader-4-line text-xl animate-spin mr-2" aria-hidden="true"></i>
          <span className="text-sm">Kullanıcılar yükleniyor...</span>
        </div>
      ) : error ? (
        <div className="rounded-lg border border-background-200 bg-background-50 p-10 flex flex-col items-center justify-center text-center">
          <span className="w-12 h-12 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-error-warning-line text-xl" aria-hidden="true"></i>
          </span>
          <p className="mt-3 font-heading font-semibold text-sm text-foreground-700">
            Kullanıcılar yüklenemedi
          </p>
          <p className="mt-1 text-sm text-foreground-500 max-w-md">{error}</p>
          <button
            type="button"
            onClick={reload}
            className="mt-4 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors"
          >
            Tekrar dene
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="ri-group-line"
          title="Kullanıcı bulunamadı"
          description="Arama kriterlerinize uyan bir kullanıcı yok. Farklı bir arama ya da filtre deneyin."
        />
      ) : (
        <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/60">
                  <th className="text-left px-4 py-3 font-semibold text-foreground-600 whitespace-nowrap">
                    Kullanıcı
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground-600 whitespace-nowrap">
                    Rol
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground-600 whitespace-nowrap">
                    Sınıf
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground-600 whitespace-nowrap">
                    İl
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground-600 whitespace-nowrap">
                    Kayıt Tarihi
                  </th>
                  <th className="text-right px-4 py-3 font-semibold text-foreground-600 whitespace-nowrap">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const meta = ROLE_META[p.role ?? ""] ?? {
                    label: p.role ?? "Bilinmiyor",
                    badge: "bg-background-100 text-foreground-600",
                    icon: "ri-user-line",
                  };
                  return (
                    <tr
                      key={p.id}
                      className="border-b border-background-100 last:border-0 hover:bg-background-100/40 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <span className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 text-xs font-semibold shrink-0">
                            {initialsOf(p.full_name ?? "?")}
                          </span>
                          <div className="min-w-0">
                            <p className="font-medium text-foreground-900 truncate">
                              {p.full_name ?? "—"}
                            </p>
                            <p className="text-xs text-foreground-500 truncate">
                              {p.email ?? "—"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${meta.badge}`}
                        >
                          <i className={`${meta.icon} text-sm`} aria-hidden="true"></i>
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-foreground-700 whitespace-nowrap">
                        {p.grade ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-foreground-700 whitespace-nowrap">
                        {p.city ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-foreground-500 whitespace-nowrap">
                        {formatDate(p.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => setDetailProfile(p)}
                            className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 hover:text-foreground-900 transition-colors cursor-pointer"
                            aria-label={`${p.full_name ?? "Kullanıcı"} detayını gör`}
                            title="Detay"
                          >
                            <i className="ri-eye-line text-base" aria-hidden="true"></i>
                          </button>
                          <button
                            type="button"
                            disabled={!!p.removed_at}
                            onClick={() => setEditProfile(p)}
                            className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 hover:text-foreground-900 transition-colors cursor-pointer"
                            aria-label={`${p.full_name ?? "Kullanıcı"} düzenle`}
                            title="Düzenle"
                          >
                            <i className="ri-pencil-line text-base" aria-hidden="true"></i>
                          </button>
                          {p.role!=="admin"&&<AccountRemoval id={p.user_id??p.id} email={p.email??""} name={p.full_name??"Kullanıcı"} kind="user" removed={!!p.removed_at} onDone={reload}/>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detay modalı */}
      <UserDetailModal
        open={!!detailProfile}
        onClose={() => setDetailProfile(null)}
        profile={detailProfile}
        onEdit={() => {
          setEditProfile(detailProfile);
          setDetailProfile(null);
        }}
      />

      {/* Düzenleme modalı */}
      <UserEditModal
        open={!!editProfile}
        onClose={() => setEditProfile(null)}
        profile={editProfile}
        onSaved={reload}
      />

      {/* Öğretmen ekleme modalı */}
      <TeacherAddModal
        open={addTeacherOpen}
        onClose={() => setAddTeacherOpen(false)}
        onSaved={reload}
      />
    </div>
  );
}
